/**
 * Clinical procedures — procedures ordered for and performed on patients.
 *
 * NOT the same as `api.procedure` (modules/fee-config.ts), which is the clinic's procedure
 * CATALOGUE at /procedures: a fee-schedule row with a name, department, base price and duration.
 * This module backs /clinical-procedures, the clinical record of a procedure actually happening.
 * The catalogue is where a procedure is *defined*; this is where one is *done*.
 */
import { fetchApi } from '../client';
import { buildQS } from '../helpers';
import type { PagedResponse } from '../../../types/pagination';

const BASE = '/api/clinical-procedures';

export type ProcedureStatus = 'ORDERED' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type ProcedureUrgency = 'ROUTINE' | 'URGENT' | 'STAT';

/**
 * `null` means NOT RECORDED and is deliberately distinct from `'NOT_APPLICABLE'`.
 *
 * The server refuses to start or complete a procedure while this is null, and the UI must show it
 * as an unanswered question rather than quietly rendering "N/A" — collapsing the two is how a
 * skipped field ends up looking like a settled one.
 */
export type Laterality = 'LEFT' | 'RIGHT' | 'BILATERAL' | 'NOT_APPLICABLE';

/** `null` means NOT RECORDED, distinct from `'NOT_REQUIRED'`. Same reasoning as `Laterality`. */
export type ConsentStatus = 'OBTAINED' | 'NOT_REQUIRED' | 'REFUSED';

export type Anaesthesia = 'NONE' | 'TOPICAL' | 'LOCAL' | 'REGIONAL' | 'SEDATION';
export type ProcedureOutcome = 'SUCCESSFUL' | 'PARTIAL' | 'ABANDONED';

export interface ClinicalProcedure {
  id: string;
  patientId: string;
  patientName?: string | null;
  patientMrn?: string | null;

  procedureCatalogueId?: string | null;
  procedureName: string;
  department?: string | null;

  appointmentId?: string | null;
  visitId?: string | null;

  orderedByUserId: string;
  orderedByName?: string | null;
  orderedAt: string;

  status: ProcedureStatus;
  urgency: ProcedureUrgency;
  indication?: string | null;

  site?: string | null;
  laterality?: Laterality | null;

  consentStatus?: ConsentStatus | null;
  consentNotes?: string | null;
  consentRecordedAt?: string | null;
  /** The signed consent form behind consentStatus OBTAINED, when it was signed in ClinVea (A4). */
  signedConsentId?: string | null;

  scheduledAt?: string | null;
  safetyCheckAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;

  performedByUserId?: string | null;
  performedByName?: string | null;
  assistedBy?: string | null;

  anaesthesia?: Anaesthesia | null;
  anaesthesiaAgent?: string | null;

  findings?: string | null;
  complications?: string | null;
  outcome?: ProcedureOutcome | null;

  specimenCollected?: boolean | null;
  specimenLabOrderId?: string | null;
  aftercareInstructions?: string | null;

  cancelledReason?: string | null;
  cancelledAt?: string | null;

  /**
   * True while the side or the consent decision is still unrecorded. Computed server-side from the
   * same condition the server's own gate uses, so the worklist badge cannot drift away from what
   * the API will actually accept.
   */
  awaitingSafetyAnswers?: boolean;

  /** Null means the instructions were written but never actually delivered to the patient. */
  aftercareSentAt?: string | null;
  aftercareMessageId?: string | null;

  roomId?: string | null;
  roomName?: string | null;
  durationMinutes?: number | null;

  /** Null means no catalogue price is known — deliberately NOT 0, which would mean free. */
  chargeAmount?: number | null;
  isBilled?: boolean | null;
  billId?: string | null;

  consumables?: ProcedureConsumable[];
  consumablesCost?: number | null;
}

export interface ProcedureConsumable {
  id: string;
  inventoryId?: string | null;
  itemName: string;
  quantity: number;
  unitCost?: number | null;
  lineTotal?: number | null;
  /**
   * False means the item was used and recorded but stock could not be reduced to match. The UI
   * must show this as a discrepancy — hiding it turns a visible bookkeeping gap into an invisible
   * one, which is the opposite of why the record exists.
   */
  stockApplied: boolean;
}

export interface ProcedureRoom {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

export interface RoomAvailability {
  available: boolean;
  conflictingProcedureId?: string;
  conflictingProcedureName?: string;
  from?: string;
  to?: string;
}

/** Sentinel that deliberately RELEASES a room, as opposed to leaving it unchanged. */
export const RELEASE_ROOM = '__none__';

export interface OrderProcedureBody {
  patientId: string;
  /** When set, the name and department are copied from the catalogue and any name here is ignored. */
  catalogueId?: string;
  /** Required only when `catalogueId` is absent. */
  procedureName?: string;
  department?: string;
  appointmentId?: string;
  visitId?: string;
  urgency?: ProcedureUrgency;
  indication?: string;
  site?: string;
  laterality?: Laterality;
  consentStatus?: ConsentStatus;
  consentNotes?: string;
  /** ISO local date-time. Present means the procedure is booked; absent leaves it in ORDERED. */
  scheduledAt?: string;
  /** Optional room. Conflict-checked server-side only when a scheduledAt is also given. */
  roomId?: string;
}

/** One item used during the procedure. Either a stock item or a free-text name. */
export interface ConsumableLine {
  inventoryId?: string;
  itemName?: string;
  quantity?: number;
}

export interface StartProcedureBody {
  identityConfirmed: boolean;
  site?: string;
  laterality?: Laterality;
  consentStatus?: ConsentStatus;
  consentNotes?: string;
}

export interface CompleteProcedureBody {
  site?: string;
  laterality?: Laterality;
  consentStatus?: ConsentStatus;
  consentNotes?: string;
  anaesthesia?: Anaesthesia;
  anaesthesiaAgent?: string;
  assistedBy?: string;
  findings?: string;
  complications?: string;
  outcome?: ProcedureOutcome;
  specimenCollected?: boolean;
  specimenLabOrderId?: string;
  aftercareInstructions?: string;
  /**
   * Tests to raise for the specimen, sent as ONE order so they share an accession. Empty with
   * `specimenCollected` true is legitimate — the specimen goes to an outside lab on paper.
   */
  specimenTests?: string[];
  /** Destination lab; omitted routes to the clinic's in-house lab, as everywhere else. */
  specimenLabId?: string;
  consumables?: ConsumableLine[];
}

export const clinicalProceduresModule = {
  /** Everything still needing action in this clinic, soonest first. */
  worklist: () => fetchApi<ClinicalProcedure[]>(BASE),

  /** Day-bounded on purpose — the completed set is unbounded, the worklist is not. */
  completed: (date?: string) =>
    fetchApi<ClinicalProcedure[]>(`${BASE}/completed${buildQS({ date })}`),

  summary: () => fetchApi<Record<string, number>>(`${BASE}/summary`),

  forPatient: (patientId: string) =>
    fetchApi<ClinicalProcedure[]>(`${BASE}/patient/${patientId}`),

  /** One page of a patient's procedures, newest order first (medopd-docs 112). */
  forPatientPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<ClinicalProcedure>>(`${BASE}/patient/${patientId}${buildQS({ page, size })}`),

  get: (id: string) => fetchApi<ClinicalProcedure>(`${BASE}/${id}`),

  order: (body: OrderProcedureBody) =>
    fetchApi<ClinicalProcedure>(BASE, { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Book a time and optionally a room.
   *
   * Omitting `roomId` LEAVES the current room rather than clearing it — on a reschedule that is
   * almost always what was meant. Pass {@link RELEASE_ROOM} to deliberately free one.
   */
  schedule: (id: string, scheduledAt: string, roomId?: string) =>
    fetchApi<ClinicalProcedure>(`${BASE}/${id}/schedule`, {
      method: 'PATCH',
      body: JSON.stringify({ scheduledAt, roomId }),
    }),

  recordConsent: (id: string, consentStatus: ConsentStatus, consentNotes?: string) =>
    fetchApi<ClinicalProcedure>(`${BASE}/${id}/consent`, {
      method: 'PATCH',
      body: JSON.stringify({ consentStatus, consentNotes }),
    }),

  /** The pre-procedure time-out. Rejected server-side unless side and consent are recorded. */
  start: (id: string, body: StartProcedureBody) =>
    fetchApi<ClinicalProcedure>(`${BASE}/${id}/start`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** The procedure note. Same safety gate as `start` — completing is not a way around it. */
  complete: (id: string, body: CompleteProcedureBody) =>
    fetchApi<ClinicalProcedure>(`${BASE}/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  cancel: (id: string, reason: string) =>
    fetchApi<ClinicalProcedure>(`${BASE}/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Rooms ──────────────────────────────────────────────────────────────────────────────────
  // An empty room list is a normal state, not a misconfiguration: rooms are optional and a clinic
  // with none schedules exactly as it did before rooms existed.

  listRooms: (activeOnly = true) =>
    fetchApi<ProcedureRoom[]>(`${BASE}/rooms${buildQS({ activeOnly })}`),

  createRoom: (body: { name: string; description?: string }) =>
    fetchApi<ProcedureRoom>(`${BASE}/rooms`, { method: 'POST', body: JSON.stringify(body) }),

  updateRoom: (id: string, body: { name?: string; description?: string; isActive?: boolean }) =>
    fetchApi<ProcedureRoom>(`${BASE}/rooms/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  /** Checks a slot without booking it, so the UI can warn while the time is still being chosen. */
  checkRoom: (roomId: string, at: string, durationMinutes?: number, excludeProcedureId?: string) =>
    fetchApi<RoomAvailability>(
      `${BASE}/rooms/${roomId}/availability${buildQS({ at, durationMinutes, excludeProcedureId })}`),

  // ── Billing ────────────────────────────────────────────────────────────────────────────────

  /** Completed procedures no bill has picked up yet — what the billing desk adds to a bill. */
  unbilled: (patientId?: string) =>
    fetchApi<ClinicalProcedure[]>(`${BASE}/unbilled${buildQS({ patientId })}`),

  /** Called once a bill including these procedures has actually been raised. */
  markBilled: (procedureIds: string[], billId?: string) =>
    fetchApi<{ marked: number }>(`${BASE}/mark-billed`, {
      method: 'POST',
      body: JSON.stringify({ procedureIds, billId }),
    }),

  /** Consumables recorded but never deducted from stock — the reconciliation worklist. */
  stockCorrections: () =>
    fetchApi<ProcedureConsumable[]>(`${BASE}/stock-corrections`),
};
